import React, { ReactElement, ReactNode } from 'react';

import { If } from '../../shared/If/If';
import { IParentProps } from '../../shared/types/IParentProps';
import { createLayoutSlot } from '../layout-slot/createLayoutSlot';
import { useSlot } from '../layout-slot/useSlot';

import styles from './OzProductCard.module.css';
import { OzProductCardHero } from './OzProductCardHero';
import { OzProductCardSubtitle } from './OzProductCardSubtitle';
import { OzProductCardTitle } from './OzProductCardTitle';
import { PRODUCT_CARD_VARIANTS } from './PRODUCT_CARD_VARIANTS';

export type IProductCardProps = IParentProps & {
    variant: PRODUCT_CARD_VARIANTS;
};

type IProductCardClassNames = {
    host: string;
    hero: string;
    body: string;
};

export enum PRODUCT_CARD_SLOTS {
    BODY,
    FOOTER,
    TAGS,
    HERO,
}

function useBaseClassNames (): IProductCardClassNames {
    return {
        host: 'relative',
        hero: '',
        body: 'font-gellix',
    };
}

function useFloatingClassNames (): IProductCardClassNames {
    return {
        host: '',
        hero: 'mb-1/2',
        body: '',
    };
}

function useContainedClassNames (): IProductCardClassNames {
    return {
        host: `${styles.contained} bg-white-500 overflow-hidden`,
        hero: '',
        body: 'p-2',
    };
}

function getKeys<TType> (obj: TType): (keyof TType)[] {
    return Object.keys(obj) as (keyof TType)[];
}

function useClassNames (variant: PRODUCT_CARD_VARIANTS): IProductCardClassNames {
    const baseClassNames: IProductCardClassNames = useBaseClassNames();
    const floatingClassNames: IProductCardClassNames = useFloatingClassNames();
    const containedClassNames: IProductCardClassNames = useContainedClassNames();
    let classNames: IProductCardClassNames = floatingClassNames;

    if (variant === PRODUCT_CARD_VARIANTS.CONTAINED) {
        classNames = containedClassNames;
    }

    return getKeys(classNames).reduce(
        (obj: IProductCardClassNames, key: keyof IProductCardClassNames): IProductCardClassNames => {
            obj[key] = obj[key] + ' ' + classNames[key];

            return obj;
        },
        { ...baseClassNames },
    );
}

export function OzProductCard (props: IProductCardProps): ReactElement {
    const { children, variant }: IProductCardProps = props;
    const body: ReactNode = useSlot(children, PRODUCT_CARD_SLOTS.BODY);
    const footer: ReactNode = useSlot(children, PRODUCT_CARD_SLOTS.FOOTER);
    const tags: ReactNode = useSlot(children, PRODUCT_CARD_SLOTS.TAGS);
    const hero: ReactNode = useSlot(children, PRODUCT_CARD_SLOTS.HERO);
    const classNames: IProductCardClassNames = useClassNames(variant);

    if (!body) {
        throw new Error('OzProductCard rendered without an OzProductCard.Body');
    }

    if (!hero) {
        throw new Error('OzProductCard rendered without an OzProductCard.Hero');
    }

    return (
        <div className={classNames.host}>
            <div className={classNames.hero}>{hero}</div>
            <If condition={!!tags}>
                <div className="absolute top-2 left-2">{tags}</div>
            </If>
            <div className={classNames.body}>{body}</div>
            <If condition={!!footer}>
                <div className="pl-2 pr-2 pb-2">{footer}</div>
            </If>
        </div>
    );
}

OzProductCard.Body = createLayoutSlot(PRODUCT_CARD_SLOTS.BODY, 'OzProductCard.Body');
OzProductCard.Footer = createLayoutSlot(PRODUCT_CARD_SLOTS.FOOTER, 'OzProductCard.Footer');
OzProductCard.Tags = createLayoutSlot(PRODUCT_CARD_SLOTS.TAGS, 'OzProductCard.Tags');
OzProductCard.Hero = createLayoutSlot(PRODUCT_CARD_SLOTS.HERO, 'OzProductCard.Hero', OzProductCardHero);
OzProductCard.Title = OzProductCardTitle;
OzProductCard.Subtitle = OzProductCardSubtitle;
